/**
 * Единая нормализация ошибок HTTP для UI.
 *
 * Цель: пользователь НИКОГДА не должен видеть «сырую» строку из `Error.message`,
 * stack trace, body ответа сервера, "400 Bad Request", "Failed to fetch", Feign,
 * SQL/Java/Kafka технических текстов. Вместо этого — стабильная русская фраза.
 *
 * Используется так:
 *
 *   try {
 *     await someFetch(...);
 *   } catch (e) {
 *     toast("error", toUserErrorMessage(e), "Контекст");
 *   }
 *
 * Также есть {@link assertOk}, который безопасно бросает {@link AppHttpError}
 * с уже нормализованным сообщением — тогда ловить можно просто через `e.message`.
 */

export type ErrorScope =
    | "broadcast"
    | "schedule"
    | "reservation"
    | "subscription"
    | "auth"
    | "admin"
    | "court"
    | "settings"
    | "generic";

const RUSSIAN_BY_STATUS: Record<number, string> = {
    400: "Сервер отклонил запрос. Проверьте введённые данные и попробуйте снова.",
    401: "Сессия истекла, войдите снова.",
    403: "Нет прав на это действие.",
    404: "Запрашиваемые данные не найдены.",
    408: "Сервер не успел ответить вовремя. Попробуйте ещё раз.",
    409: "Конфликт данных: возможно, что-то уже изменилось. Обновите страницу.",
    413: "Файл слишком большой.",
    415: "Неподдерживаемый формат файла.",
    422: "Сервер не принял данные. Проверьте поля и попробуйте снова.",
    429: "Слишком много запросов, подождите немного и попробуйте снова.",
    500: "Внутренняя ошибка сервера. Повторите попытку чуть позже.",
    502: "Сервис временно недоступен.",
    503: "Сервис временно недоступен.",
    504: "Сервис не отвечает. Попробуйте позже.",
};

const SCOPE_FALLBACK: Record<ErrorScope, string> = {
    broadcast: "Не удалось выполнить операцию с рассылкой. Попробуйте ещё раз.",
    schedule:  "Не удалось выполнить операцию с расписанием. Попробуйте ещё раз.",
    reservation: "Не удалось выполнить операцию по заявке. Попробуйте ещё раз.",
    subscription: "Не удалось выполнить операцию по абонементу. Попробуйте ещё раз.",
    auth: "Не удалось выполнить вход. Проверьте данные и попробуйте снова.",
    admin: "Не удалось выполнить операцию. Попробуйте ещё раз.",
    court: "Не удалось выполнить операцию по корту. Попробуйте ещё раз.",
    settings: "Не удалось выполнить операцию. Попробуйте ещё раз.",
    generic: "Что-то пошло не так. Попробуйте ещё раз.",
};

export class AppHttpError extends Error {
    /** Стабильное сообщение для UI. */
    public readonly userMessage: string;
    /** HTTP-статус (если ошибка возникла из ответа). */
    public readonly status?: number;
    /** Сырой ответ/детали — только для логов / details, в основной UI не выводим. */
    public readonly details?: string;

    constructor(userMessage: string, opts?: { status?: number; details?: string }) {
        super(userMessage);
        this.name = "AppHttpError";
        this.userMessage = userMessage;
        this.status = opts?.status;
        this.details = opts?.details;
    }
}

function isNetworkLikeMessage(msg: string | undefined | null): boolean {
    if (!msg) return false;
    const m = msg.toLowerCase();
    return (
        m.includes("failed to fetch") ||
        m.includes("networkerror") ||
        m.includes("load failed") ||
        m.includes("network request failed") ||
        m.includes("typeerror: failed") ||
        m.includes("err_network") ||
        m.includes("err_connection")
    );
}

/**
 * Преобразовать любой брошенный объект в безопасное русское сообщение для UI.
 * Никогда не возвращает status code, body, имя класса исключения и т.п.
 */
export function toUserErrorMessage(err: unknown, scope: ErrorScope = "generic"): string {
    if (err instanceof AppHttpError) {
        return err.userMessage || SCOPE_FALLBACK[scope];
    }

    if (typeof err === "object" && err !== null) {
        const anyErr = err as { message?: string; status?: number };
        if (typeof anyErr.status === "number" && RUSSIAN_BY_STATUS[anyErr.status]) {
            return RUSSIAN_BY_STATUS[anyErr.status];
        }
        if (isNetworkLikeMessage(anyErr.message)) {
            return "Сервис временно недоступен. Проверьте интернет и попробуйте снова.";
        }
    }

    if (err instanceof Error && isNetworkLikeMessage(err.message)) {
        return "Сервис временно недоступен. Проверьте интернет и попробуйте снова.";
    }

    return SCOPE_FALLBACK[scope];
}

/**
 * Проверить ответ {@link Response}; в случае ошибки — бросить {@link AppHttpError}
 * с уже нормализованным русским сообщением. Тело ответа сохранится в `details`
 * и НЕ попадёт пользователю в основной UI.
 *
 * @param scope даёт более конкретный fallback, когда сервер не вернул понятный текст
 */
export async function assertOk(res: Response, scope: ErrorScope = "generic"): Promise<Response> {
    if (res.ok) return res;
    let raw = "";
    try {
        raw = await res.text();
    } catch {
        // body unreadable — ничего страшного
    }
    let serverMessage: string | undefined;
    if (raw) {
        try {
            const j = JSON.parse(raw);
            if (j && typeof j === "object") {
                if (typeof j.userMessage === "string" && j.userMessage.trim()) serverMessage = j.userMessage.trim();
                else if (typeof j.message === "string" && j.message.trim()) serverMessage = j.message.trim();
                else if (typeof j.error === "string" && j.error.trim()) serverMessage = j.error.trim();
            }
        } catch {
            // не JSON — игнорируем
        }
    }

    // ВАЖНО: сообщения от Spring/Feign на английском с тех. деталями ("Bad Request",
    // "Internal Server Error", "No max_chat_id", "feign…") НЕ показываем пользователю.
    // Если строка целиком ASCII (значит, скорее всего англ./тех.) — не пропускаем как user-facing.
    const isAsciiOnly = !!serverMessage && /^[\x00-\x7F]+$/.test(serverMessage);
    const userMessage =
        serverMessage && !isAsciiOnly && !looksTechnicalKeyword(serverMessage)
            ? serverMessage
            : RUSSIAN_BY_STATUS[res.status] ?? SCOPE_FALLBACK[scope];

    throw new AppHttpError(userMessage, { status: res.status, details: raw || undefined });
}

function looksTechnicalKeyword(s: string): boolean {
    const m = s.toLowerCase();
    return (
        m.includes("exception") ||
        m.includes("stack trace") ||
        m.includes("nullpointer") ||
        m.includes("feign") ||
        m.includes("sqlstate") ||
        m.includes("constraint") ||
        m.includes("kafka") ||
        m.includes("hibernate") ||
        m.includes("psqlexception") ||
        m.includes("bad request") ||
        m.includes("internal server error")
    );
}
